/**
 * =============================================================================
 * PATIENT JOURNEY - TAB SHELL
 * =============================================================================
 *
 * Owns the four tabs on the case details page and the case tracking workflow.
 * The three information tabs live in `CaseInfoPanels`, the workflow state in
 * `useCaseWorkflow`.
 * =============================================================================
 */

import React, { useCallback, useMemo, useState } from "react";
import { Box, Typography, Skeleton } from "@mui/material";
import HistoryToggleOffIcon from "@mui/icons-material/HistoryToggleOff";
import moment from "moment";
import { toast } from "sonner";

import JourneyProgress from "./JourneyProgress";
import JourneySections from "./JourneySections";
import useCaseWorkflow from "./useCaseWorkflow";
import useJourneyConfirmations from "./useJourneyConfirmations";
import JourneyConfirmDialog from "./JourneyConfirmDialog";

import {
    CareTeamPanel,
    CaseLogPanel,
    ContactDetailsPanel,
} from "./CaseInfoPanels";

import {
    getJourneyDocuments,
    openJourneyDocument,
} from "./caseDataAdapters";
import { FONT, JOURNEY_COLORS, sectionTitleSx } from "./journeyStyles";

import { useGetCaseJourney, submitJourneyStep } from "../../../api/cases";

/*
 * ============================================================
 * TABS
 * ============================================================
 */

const TABS = [
    {
        id: "journey",
        label: "Patient Journey",
    },
    {
        id: "contacts",
        label: "Contact Details",
    },
    {
        id: "care-team",
        label: "Care Team",
    },
    {
        id: "case-log",
        label: "Case Log",
    },
];

/*
 * ============================================================
 * LOADING
 *
 * Holds the shape of the progress bar and the first two cards so the panel
 * does not jump when the journey arrives.
 * ============================================================
 */

const JourneySkeleton = () => (
    <Box sx={{ width: "100%" }}>
        <Box
            sx={{
                display: "flex",
                gap: "8px",
                marginBottom: "14px",
            }}
        >
            {[0, 1, 2, 3, 4, 5].map((index) => (
                <Skeleton
                    key={index}
                    variant="rectangular"
                    height={18}
                    sx={{ flex: 1, borderRadius: "9px" }}
                />
            ))}
        </Box>

        {[0, 1].map((index) => (
            <Skeleton
                key={index}
                variant="rectangular"
                height={index === 0 ? 190 : 74}
                sx={{ borderRadius: "10px", marginBottom: "12px" }}
            />
        ))}
    </Box>
);

/*
 * ============================================================
 * NOTHING TO SHOW
 *
 * A case closed without a single step recorded - closed before this workflow
 * existed, or through the plain close endpoint. Left alone the stepper would
 * draw an empty progress bar and six locked placeholders, which reads as a
 * journey still to come. It never is: the case is closed.
 * ============================================================
 */

const JourneyUnavailable = ({ closedAt }) => {
    const closedOn =
        closedAt && moment(closedAt).isValid()
            ? moment(closedAt).format("DD/MM/YYYY")
            : null;

    return (
        <Box
            sx={{
                display: "flex",
                alignItems: "flex-start",
                gap: "10px",
                padding: { xs: "14px", sm: "16px" },
                borderRadius: "8px",
                border: "1px solid " + JOURNEY_COLORS.border,
                backgroundColor: JOURNEY_COLORS.surfaceMuted,
                boxSizing: "border-box",
            }}
        >
            <HistoryToggleOffIcon
                sx={{
                    fontSize: "20px",
                    color: JOURNEY_COLORS.textMuted,
                    flexShrink: 0,
                }}
            />

            <Box sx={{ minWidth: 0 }}>
                <Typography
                    sx={{
                        fontFamily: FONT,
                        fontSize: "13px",
                        lineHeight: "18px",
                        fontWeight: 700,
                        color: JOURNEY_COLORS.text,
                    }}
                >
                    Patient journey is not available for this case
                </Typography>

                <Typography
                    sx={{
                        fontFamily: FONT,
                        fontSize: "12px",
                        lineHeight: "18px",
                        color: JOURNEY_COLORS.textMuted,
                        marginTop: "2px",
                    }}
                >
                    This case was closed
                    {closedOn ? " on " + closedOn : ""} without any journey
                    steps being recorded, so there is nothing to show here.
                </Typography>
            </Box>
        </Box>
    );
};

/*
 * ============================================================
 * COMPONENT
 * ============================================================
 */

const PatientJourney = ({
    caseData,
    reportId,
    peoples = [],
    doctorsLoading = false,
    onStepSave,
}) => {
    const [activeTab, setActiveTab] = useState("journey");

    /*
     * ------------------------------------------------------------
     * WORKFLOW
     *
     * The backend owns the workflow: it returns the steps, the available
     * actions, the transitions and the progress, and it validates and records
     * every answer. This component renders what comes back.
     * ------------------------------------------------------------
     */

    const caseReportId = reportId || caseData?.report_id;

    const { journey, isLoading, mutate } = useGetCaseJourney(caseReportId);

    const handleSubmit = useCallback(
        (body) => submitJourneyStep(caseReportId, body),
        [caseReportId]
    );

    const workflow = useCaseWorkflow({
        journey,
        isLoading,
        recordedBy:
            caseData?.referred_by_admin_name ||
            caseData?.referred_to_admin_name ||
            "",
        referredAt: caseData?.referred_on || null,
        onSubmit: handleSubmit,
        onStepSave: (entry, context) => {
            /* Keep the shared cache honest for anything else showing this case. */
            mutate();

            if (typeof onStepSave === "function") {
                onStepSave(entry, context);
            }
        },
        onError: (message) => toast.error(message),
    });

    /*
     * ------------------------------------------------------------
     * CONFIRMATIONS
     *
     * The same dialogs the Close Case popup shows. There is no popup to
     * dismiss here, so only the midway warning and the closed confirmation
     * ever appear.
     * ------------------------------------------------------------
     */

    const { dialogProps, submitStep } = useJourneyConfirmations({
        workflow,
        caseData,
    });

    /*
     * ------------------------------------------------------------
     * DOCUMENTS
     * ------------------------------------------------------------
     */

    const journeyDocuments = useMemo(
        () => getJourneyDocuments(caseData, workflow.documents),
        [caseData, workflow.documents]
    );

    /*
     * ------------------------------------------------------------
     * RENDER
     * ------------------------------------------------------------
     */

    return (
        <Box
            sx={{
                width: "100%",
                fontFamily: FONT,
            }}
        >
            {/* ========================================================
                TABS
            ======================================================== */}

            {/* The page sits on --app-bg, which is the same colour as
                --app-surface-2 in the light theme, so the track uses
                --app-border to stay visible in both themes. */}

            <Box
                role="tablist"
                sx={{
                    width: "100%",
                    height: "48px",
                    boxSizing: "border-box",
                    borderRadius: "64px",
                    backgroundColor: JOURNEY_COLORS.track,
                    display: "flex",
                    alignItems: "center",
                    padding: "4px",
                    marginBottom: { xs: "16px", sm: "24px" },
                    /* Four labels cannot share a phone width without being
                       ellipsised to nothing, so below sm the strip keeps each
                       label at its natural size and scrolls instead. */
                    overflowY: "hidden",
                    overflowX: { xs: "auto", sm: "hidden" },
                    scrollbarWidth: "none",
                    "&::-webkit-scrollbar": { display: "none" },
                }}
            >
                {TABS.map((tab) => {
                    const isSelected = activeTab === tab.id;

                    return (
                        <Box
                            key={tab.id}
                            role="tab"
                            tabIndex={0}
                            aria-selected={isSelected}
                            onClick={() => setActiveTab(tab.id)}
                            onKeyDown={(event) => {
                                if (
                                    event.key === "Enter" ||
                                    event.key === " "
                                ) {
                                    event.preventDefault();
                                    setActiveTab(tab.id);
                                }
                            }}
                            sx={{
                                flex: { xs: "0 0 auto", sm: 1 },
                                minWidth: 0,
                                height: "40px",
                                paddingX: { xs: "16px", sm: "12px" },
                                borderRadius: "64px",
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                                cursor: "pointer",
                                userSelect: "none",
                                outline: "none",
                                backgroundColor: isSelected
                                    ? JOURNEY_COLORS.accent
                                    : "transparent",
                                transition: "background-color 0.2s ease",
                                "&:hover": {
                                    backgroundColor: isSelected
                                        ? JOURNEY_COLORS.accent
                                        : JOURNEY_COLORS.surface,
                                },
                            }}
                        >
                            <Typography
                                sx={{
                                    fontFamily: FONT,
                                    fontSize: "14px",
                                    lineHeight: "20px",
                                    fontWeight: isSelected ? 600 : 500,
                                    letterSpacing: "-0.01em",
                                    color: isSelected
                                        ? "#FFFFFF"
                                        : JOURNEY_COLORS.textMuted,
                                    whiteSpace: "nowrap",
                                    overflow: "hidden",
                                    textOverflow: "ellipsis",
                                }}
                            >
                                {tab.label}
                            </Typography>
                        </Box>
                    );
                })}
            </Box>

            {/* ========================================================
                PATIENT JOURNEY
            ======================================================== */}

            {activeTab === "journey" && (
                <Box sx={{ width: "100%" }}>
                    <Typography sx={sectionTitleSx}>
                        Patient Journey
                    </Typography>

                    {workflow.isLoading && !workflow.isReady ? (
                        <JourneySkeleton />
                    ) : !workflow.hasJourney ? (
                        <JourneyUnavailable closedAt={workflow.closedAt} />
                    ) : (
                        <>
                            <JourneyProgress
                                stages={workflow.stages}
                                stageStatuses={workflow.stageStatuses}
                            />

                            <Box
                                sx={{
                                    width: "100%",
                                    marginTop: "10px",
                                }}
                            >
                                <JourneySections
                                    workflow={workflow}
                                    documents={journeyDocuments}
                                    isSaving={workflow.isSubmitting}
                                    onDocumentDownload={openJourneyDocument}
                                    onSubmitStep={submitStep}
                                />
                            </Box>
                        </>
                    )}
                </Box>
            )}

            {/* ========================================================
                CONTACT DETAILS
            ======================================================== */}

            {activeTab === "contacts" && (
                <ContactDetailsPanel caseData={caseData} />
            )}

            {/* ========================================================
                CARE TEAM
            ======================================================== */}

            {activeTab === "care-team" && (
                <CareTeamPanel
                    caseData={caseData}
                    peoples={peoples}
                    isLoading={doctorsLoading}
                />
            )}

            {/* ========================================================
                CASE LOG
            ======================================================== */}

            {activeTab === "case-log" && (
                <CaseLogPanel caseData={caseData} />
            )}

            {/* ========================================================
                CONFIRMATIONS - one dialog, three configurations
            ======================================================== */}

            <JourneyConfirmDialog {...dialogProps} />
        </Box>
    );
};

export default PatientJourney;
